import { Fragment, useCallback, useState } from 'react';

import coin from '~/assets/icons/coin.svg';
import percent from '~/assets/icons/percent.svg';
import { rage, useObservable } from '~/rage';
import { BankOperationType, Currency, WalletType } from '~/shared';

import cash from '~/assets/icons/cash.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import {
	setWithdrawValue,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { MoneyButton } from '../MoneyButton';
import { MoneyInput } from '../MoneyInput';
import { SectionHeading } from '../SectionHeading';
import {
	StyledButtonContent,
	StyledContainer,
	StyledMoneyButtonsContainer,
	StyledMoneyButtonsFirstRow,
	StyledMoneyButtonsSecondRow,
	StyledSectionContainer,
	StyledSummaryContainer,
} from './WithdrawMoney.styled';

const WithdrawMoney: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const value = useLayoutSelector((state) => state.bank.withdrawValue);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const fee = useObservable(
		rage.bank.getTransferFee(BankOperationType.WITHDRAW),
	);

	const withdraw = useCallback(async () => {
		setIsLoading(true);
		try {
			await rage.bank.exchange(WalletType.Bank, WalletType.Cash, value);
		} catch (e) {
			// @TODO: какой-то вывод ошибки
		} finally {
			setIsLoading(false);
		}
	}, [value]);

	return (
		<StyledContainer>
			<StyledSectionContainer>
				<SectionHeading icon={coin} text={'Введите сумму снятия'} />
				<MoneyInput
					value={value}
					changeHandler={(value: number) => dispatch(setWithdrawValue(value))}
					currency={Currency.Ruble}
				/>
				<StyledMoneyButtonsContainer>
					<StyledMoneyButtonsFirstRow>
						<MoneyButton
							text={'1 000'}
							onClick={() => dispatch(setWithdrawValue(value + 1000))}
						/>
						<MoneyButton
							text={'10 000'}
							onClick={() => dispatch(setWithdrawValue(value + 10000))}
						/>
						<MoneyButton
							text={'100 000'}
							onClick={() => dispatch(setWithdrawValue(value + 100000))}
						/>
					</StyledMoneyButtonsFirstRow>
					<StyledMoneyButtonsSecondRow>
						<MoneyButton
							text={'1 000 000'}
							onClick={() => dispatch(setWithdrawValue(value + 1000000))}
						/>
						<MoneyButton
							text={'10 000 000'}
							onClick={() => dispatch(setWithdrawValue(value + 10000000))}
						/>
					</StyledMoneyButtonsSecondRow>
					<MoneyButton
						text={'100 000 000'}
						onClick={() => dispatch(setWithdrawValue(value + 100000000))}
					/>
				</StyledMoneyButtonsContainer>
			</StyledSectionContainer>
			<StyledSummaryContainer>
				<Text>Вы получите</Text>
				<Text>{Math.floor(value - value * fee).toLocaleString('ru-RU')}</Text>
				<CurrencyIcon icon={rubleIcon} size="sm" />
				{fee ? (
					<Fragment>
						<Text>Комиссия банка</Text>
						<Text>{(fee * 100).toLocaleString('ru-RU')}</Text>
						<img src={percent} width={14} height={14} />
					</Fragment>
				) : null}
			</StyledSummaryContainer>
			<Button
				color={ButtonColors.Select2}
				height={ButtonHeight.Height60}
				disabled={isLoading || value === 0}
				onClick={withdraw}
				style={{ position: 'absolute', bottom: 0, width: '100%', fontSize: 18 }}
			>
				<StyledButtonContent>
					{value !== 0 && <img src={cash} width={20} height={20} />}
					<Text>{value ? 'Снять наличные' : 'Введите сумму снятия'}</Text>
				</StyledButtonContent>
			</Button>
		</StyledContainer>
	);
};

export { WithdrawMoney };
